/**
 * Cloud implementation of the shared HeatmapApi. Node-scoped: binds the currently-selected node
 * id, like the attendance/groups/models adapters.
 *
 * All three reads reach the node through the tunnel, but by different routes — heatmap metrics
 * via the monitoring analytics passthrough (which is how the cloud heatmap view already reads
 * them), cameras and model assignments via the node proxy.
 */
import type { HeatmapApi, CameraRef } from '@trinetra/shared';
import { cameraIdsForModel } from '@trinetra/shared';
import { getHeatmapAggregated, getHeatmapCameraSummary, getNodeCameras, getNodeModelAssignments } from '../services/api.service';
import { selectedNodeId as nodeId } from './node';

/** Node id or '' — the shared page renders an empty state when no node is picked. */
const nid = () => nodeId() || '';

function toArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  return res?.data ?? res?.cameras ?? [];
}

export const cloudHeatmapApi: HeatmapApi = {
  async cameraSummary(params) {
    const id = nid();
    if (!id) return [];
    return getHeatmapCameraSummary(id, params);
  },

  async aggregated(cameraId, from, to) {
    const id = nid();
    if (!id) return null;
    return getHeatmapAggregated(id, cameraId, String(from), String(to));
  },

  async listCameras() {
    const id = nid();
    if (!id) return [];
    return toArray(await getNodeCameras(id)) as CameraRef[];
  },

  /**
   * Deliberately NOT caught: the report reads a rejection as "attachments unknown" and stays
   * quiet about them, whereas an empty array would claim no camera has the heatmap model —
   * printing "model not attached" on every row of a working deployment.
   */
  async heatmapCameraIds() {
    const id = nid();
    if (!id) return [];
    return cameraIdsForModel(await getNodeModelAssignments(id), 'heatmap');
  },
};
